import {
  PRESTASHOP_INTEGRATION_WITH_FAIRE_ID,
  WOOCOMMERCE_INTEGRATION_WITH_FAIRE_ID,
} from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const ImportProductsWithIntegrationsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="We integrate with multiple platforms, like Shopify, WooCommerce, and PrestaShop, to streamline your workflow on Faire."
          description={{
            text: "Introduction to importing products with integrations",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To import your products from Shopify, follow these steps:"
          description={{
            text: "Header for Shopify import steps",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  ProductsLinkFormatting(chunks),
              }}
              description={{
                text: "First step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the <BoldFormatting>Import from Shopify</BoldFormatting> button, or go to <BoldFormatting>Add products</BoldFormatting> dropdown and select <BoldFormatting>Import from Shopify</BoldFormatting> there."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Second step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="In the new window, log in with your Shopify credentials."
              description={{
                text: "Third step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once you’ve logged in, choose which products to publish to Faire. You can select <BoldFormatting>All products</BoldFormatting>, choose by <BoldFormatting>tag category</BoldFormatting>, or make a <BoldFormatting>custom selection</BoldFormatting>."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Fourth step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="a">
            <Typography>
              <StrictLocalMsg
                defaultMessage="If you choose <BoldFormatting>by tag category</BoldFormatting>, you can select the desired tag categories for import from the populated list."
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  text: "First substep of fourth step to import products from Shopify",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="b">
            <Typography>
              <StrictLocalMsg
                defaultMessage="If you choose a <BoldFormatting>custom selection</BoldFormatting>, you’ll select which products and variants you want to import on the next step."
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  text: "Second substep of fourth step to import products from Shopify",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Adjust the price, description, details, or inventory, then select <BoldFormatting>Publish to Faire</BoldFormatting>."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Fifth step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="You’ll be redirected to Faire, where you’ll see the <BoldFormatting>import status</BoldFormatting> and next steps in a sidebar."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Sixth step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={7}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="After reviewing your new listings, select <BoldFormatting>Save edits</BoldFormatting> in the top right-hand corner, then choose <BoldFormatting>Publish products</BoldFormatting> or <BoldFormatting>Save without publishing</BoldFormatting> to save these products as a draft."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Seventh step to import products from Shopify",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For information on adding products with WooCommerce or PrestaShop, please see our Help Center articles, <WooCommerceArticleLinkFormatting>WooCommerce Integration with Faire</WooCommerceArticleLinkFormatting> and <PrestaShopArticleLinkFormatting>PrestaShop Integration with Faire</PrestaShopArticleLinkFormatting>."
          values={{
            WooCommerceArticleLinkFormatting,
            PrestaShopArticleLinkFormatting,
          }}
          description={{
            text: "Information on WooCommerce and PrestaShop integrations",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const WooCommerceArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(WOOCOMMERCE_INTEGRATION_WITH_FAIRE_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const PrestaShopArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(PRESTASHOP_INTEGRATION_WITH_FAIRE_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
